import React, { useState } from "react";
import { useDangerConfirm } from "../../../shared/context/DangerConfirmContext.jsx";
import { BottomSheet } from "../../../shared/components/BottomSheet.jsx";
import { Badge } from "../../../shared/components/Badge.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { formatAppDate } from "../../../shared/utils/dateUtils.js";
import { PaymentReceiptModal } from "../../invoices/components/PaymentReceiptModal.jsx";
import { VendorPaymentModal } from "./VendorPaymentModal.jsx";
import { useBillPaymentCorrections } from "../../invoices/components/BillPaymentCorrections.jsx";
import { BillHistorySheet } from "../../../shared/components/BillHistorySheet.jsx";
import { PurchasePrintModal } from "./PurchasePrintModal.jsx";
import { getPurchaseRevisions } from "../services/purchaseService.js";

/** Purchase bill detail as a sheet/dialog, or inline in the desktop split view (variant="panel"). */
export function PurchaseDetailModal({ isOpen, onClose, purchase, onRecordPayment, onDeletePurchase, onPaymentsChanged, onEditPurchase, variant }) {
  const { confirmDanger } = useDangerConfirm();
  const [paymentAmount, setPaymentAmount] = useState("");
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [showPaymentBox, setShowPaymentBox] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedReceiptPayment, setSelectedReceiptPayment] = useState(null);
  const [showVendorTallyModal, setShowVendorTallyModal] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Edit / void / reset payments made against this bill (closes the sheet so fresh data loads)
  const paymentCorrections = useBillPaymentCorrections({
    billType: "purchase",
    bill: purchase,
    onChanged: async () => {
      if (onPaymentsChanged) await onPaymentsChanged();
      onClose();
    }
  });

  if (!purchase) return null;

  const isNonGst = purchase.billType === "Normal" || purchase.isNonGst;
  const billTitle = isNonGst ? "Vendor Bill / Cash Memo" : "GST Purchase Bill";
  const statusVariant = purchase.paymentStatus === "Paid" ? "success" : (purchase.paymentStatus === "Partial" ? "warning" : "danger");
  const isPaid = purchase.paymentStatus === "Paid";
  const balance = Number(purchase.balance || 0);

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    const amt = Number(paymentAmount || 0);
    if (amt <= 0) return;

    setRecordingPayment(true);
    try {
      await onRecordPayment(purchase.id, {
        paidAmount: amt,
        vendorId: purchase.vendorId
      });
      setShowPaymentBox(false);
      setPaymentAmount("");
      onClose();
    } catch (err) {
      alert(err.message || "Failed to record payment");
    } finally {
      setRecordingPayment(false);
    }
  };

  const handleDeleteClick = async () => {
    const confirmMsg = `Are you sure you want to delete Purchase Bill #${purchase.vendorBillNo || purchase.id} from ${purchase.vendorName}?\n\n⚠️ This will AUTOMATICALLY REVERSE and DEDUCT the inward stock from inventory!`;
    if (!(await confirmDanger({ title: `Delete bill ${purchase.vendorBillNo || ""}?`, message: confirmMsg }))) return;

    setDeleting(true);
    try {
      if (onDeletePurchase) {
        await onDeletePurchase(purchase.id);
      }
      onClose();
    } catch (err) {
      alert(err.message || "Failed to delete purchase bill");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
    <BottomSheet isOpen={isOpen} onClose={onClose} variant={variant} title={`${billTitle} • ${purchase.vendorBillNo || "No Ref"}`}>
      <div className="purchase-detail-view" style={{ fontSize: "0.85rem" }}>
        {/* Header Summary */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "1rem",
          paddingBottom: "0.75rem",
          borderBottom: "1px solid var(--border-subtle)"
        }}>
          <div>
            <div style={{ fontSize: "1.05rem", fontWeight: "700", color: "var(--text-main)" }}>
              {purchase.vendorName}
            </div>
            {purchase.vendorGstin && (
              <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", marginTop: "2px" }}>
                GSTIN: {purchase.vendorGstin}
              </div>
            )}
            {purchase.vendorPhone && (
              <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", marginTop: "2px" }}>
                📞 {purchase.vendorPhone}
              </div>
            )}
            <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", marginTop: "2px" }}>
              🏢 Received By: {purchase.companyName || "Main Company"}
            </div>
          </div>

          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "0.75rem", color: "var(--text-dim)" }}>
              BILL DATE: <strong>{formatAppDate(purchase.billDate, purchase.companyDetails?.dateFormat)}</strong>
            </div>
            <div style={{ marginTop: "4px" }}>
              <Badge variant={statusVariant}>
                {purchase.paymentStatus || "Unpaid"}
              </Badge>
            </div>
            {Number(purchase.editCount || 0) > 0 && (
              <button
                type="button"
                onClick={() => setShowHistory(true)}
                title="See earlier versions"
                style={{ marginTop: "4px", fontSize: "0.72rem", fontWeight: 600, padding: "1px 8px", borderRadius: 10, cursor: "pointer", background: "rgba(245, 158, 11, 0.12)", color: "var(--warning, #b45309)", border: "1px solid rgba(245, 158, 11, 0.4)" }}
              >
                ✏️ Edited {purchase.editCount}×
              </button>
            )}
          </div>
        </div>

        {/* Financial Summary Cards */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", marginBottom: "1rem" }}>
          <div style={{ background: "var(--bg-card)", padding: "0.75rem", borderRadius: "var(--radius-md)", border: "1px solid var(--border-subtle)" }}>
            <div style={{ fontSize: "0.75rem", color: "var(--text-dim)" }}>Total Inward Bill</div>
            <div style={{ fontSize: "1.2rem", fontWeight: "800", color: "var(--text-main)" }}>
              ₹{Number(purchase.grandTotal || 0).toLocaleString("en-IN")}
            </div>
          </div>
          <div style={{ background: "var(--bg-card)", padding: "0.75rem", borderRadius: "var(--radius-md)", border: "1px solid var(--border-subtle)" }}>
            <div style={{ fontSize: "0.75rem", color: "var(--text-dim)" }}>Balance Payable</div>
            <div style={{ fontSize: "1.2rem", fontWeight: "800", color: balance > 0 ? "var(--danger)" : "var(--success)" }}>
              ₹{balance.toLocaleString("en-IN")}
            </div>
          </div>
        </div>

        {/* Line Items Table */}
        <div style={{ marginBottom: "1rem" }}>
          <div style={{ fontWeight: "700", marginBottom: "0.4rem", color: "var(--text-main)" }}>
            📦 Received Items ({purchase.lineItems?.length || 0})
          </div>
          <div style={{ border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8rem", textAlign: "left" }}>
              <thead>
                <tr style={{ background: "var(--bg-card)", borderBottom: "1px solid var(--border-subtle)" }}>
                  <th style={{ padding: "6px 8px" }}>Item</th>
                  <th style={{ padding: "6px 8px", textAlign: "center" }}>Qty</th>
                  <th style={{ padding: "6px 8px", textAlign: "right" }}>Cost</th>
                  <th style={{ padding: "6px 8px", textAlign: "right" }}>Tax</th>
                  <th style={{ padding: "6px 8px", textAlign: "right" }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {(purchase.lineItems || []).map((li, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                    <td style={{ padding: "6px 8px" }}>
                      <div style={{ fontWeight: "600" }}>{li.itemName}</div>
                      {li.gstCode && <div style={{ fontSize: "0.7rem", color: "var(--text-dim)" }}>HSN: {li.gstCode}</div>}
                    </td>
                    <td style={{ padding: "6px 8px", textAlign: "center", fontWeight: "600" }}>
                      {li.qty} {li.unit || "pcs"}
                    </td>
                    <td style={{ padding: "6px 8px", textAlign: "right" }}>
                      ₹{li.rate}
                    </td>
                    <td style={{ padding: "6px 8px", textAlign: "right", color: "var(--text-dim)" }}>
                      {li.gstPercent > 0 ? `${li.gstPercent}%` : "0%"}
                    </td>
                    <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: "700" }}>
                      ₹{Number(li.lineTotal).toLocaleString("en-IN")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Tax Breakdown */}
        <div style={{ background: "var(--bg-card)", padding: "0.75rem", borderRadius: "var(--radius-md)", marginBottom: "1rem", border: "1px solid var(--border-subtle)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
            <span style={{ color: "var(--text-dim)" }}>Subtotal</span>
            <span>₹{Number(purchase.subtotal || 0).toLocaleString("en-IN")}</span>
          </div>
          {Number(purchase.discountAmount || 0) > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", color: "var(--success)" }}>
              <span>Discount</span>
              <span>-₹{Number(purchase.discountAmount).toLocaleString("en-IN")}</span>
            </div>
          )}
          {purchase.cgst > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", color: "var(--text-dim)" }}>
              <span>CGST</span>
              <span>₹{purchase.cgst.toLocaleString("en-IN")}</span>
            </div>
          )}
          {purchase.sgst > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", color: "var(--text-dim)" }}>
              <span>SGST</span>
              <span>₹{purchase.sgst.toLocaleString("en-IN")}</span>
            </div>
          )}
          {purchase.igst > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", color: "var(--text-dim)" }}>
              <span>IGST</span>
              <span>₹{purchase.igst.toLocaleString("en-IN")}</span>
            </div>
          )}
          {Number(purchase.tcsAmount || 0) > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", color: "var(--text-dim)" }}>
              <span>TCS{Number(purchase.tcsRate || 0) > 0 ? ` (${purchase.tcsRate}%)` : ""}</span>
              <span>₹{Number(purchase.tcsAmount).toLocaleString("en-IN")}</span>
            </div>
          )}
          {Number(purchase.roundOff || 0) !== 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", color: "var(--text-dim)" }}>
              <span>Round Off</span>
              <span>₹{Number(purchase.roundOff).toLocaleString("en-IN")}</span>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "700", paddingTop: "4px", borderTop: "1px solid var(--border-subtle)", fontSize: "0.95rem" }}>
            <span>Grand Total</span>
            <span style={{ color: "var(--accent-blue)" }}>₹{Number(purchase.grandTotal || 0).toLocaleString("en-IN")}</span>
          </div>
        </div>

        {/* Payment History Section */}
        {((purchase.paymentHistory || []).length > 0 || paymentCorrections.unrecordedAmount > 0) && (
          <div style={{ marginBottom: "1rem" }}>
            <div style={{ fontSize: "0.72rem", fontWeight: "700", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "0.4rem" }}>
              💳 Payment History ({(purchase.paymentHistory || []).length})
            </div>
            <div style={{ border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", overflow: "hidden" }}>
              {(purchase.paymentHistory || []).map((ph, idx) => (
                <div
                  key={ph.paymentId || idx}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "0.55rem 0.75rem",
                    borderBottom: idx < (purchase.paymentHistory || []).length - 1 ? "1px solid var(--border-subtle)" : "none",
                    background: "var(--bg-card)",
                    opacity: ph.voided ? 0.6 : 1
                  }}
                >
                  <div style={{ fontSize: "0.78rem", minWidth: 0 }}>
                    <div style={{ fontWeight: "600", color: "var(--text-main)" }}>
                      {ph.paymentNo || "Payment"}
                      {ph.voided && <span style={{ marginLeft: "6px", color: "var(--danger)", fontWeight: "800", fontSize: "0.7rem" }}>VOID</span>}
                    </div>
                    <div style={{ color: "var(--text-dim)", marginTop: "1px" }}>
                      {formatAppDate(ph.paymentDate, purchase.companyDetails?.dateFormat)} • {ph.paymentMode || "Cash"}
                      {ph.reference && ` • ${ph.reference}`}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
                    <span style={{ fontWeight: "800", color: ph.voided ? "var(--text-dim)" : "var(--danger)", fontSize: "0.9rem", textDecoration: ph.voided ? "line-through" : "none" }}>
                      -₹{Number(ph.amount || 0).toLocaleString("en-IN")}
                    </span>
                    {paymentCorrections.renderRowActions(ph)}
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setSelectedReceiptPayment({
                        id: ph.paymentId,
                        paymentNo: ph.paymentNo,
                        type: "PAYMENT_OUT",
                        amount: ph.amount,
                        paymentDate: ph.paymentDate,
                        paymentMode: ph.paymentMode,
                        reference: ph.reference,
                        vendorName: purchase.vendorName,
                        vendorPhone: purchase.vendorPhone || "",
                        allocations: [{
                          purchaseId: purchase.id,
                          billNo: purchase.vendorBillNo,
                          billDate: purchase.billDate,
                          grandTotal: purchase.grandTotal,
                          allocatedAmount: ph.amount,
                          remainingBalance: 0
                        }]
                      })}
                      style={{ fontSize: "0.7rem", padding: "2px 7px" }}
                    >
                      📄 Receipt
                    </Button>
                  </div>
                </div>
              ))}
              {paymentCorrections.unrecordedRow}
            </div>
          </div>
        )}

        {/* Payment Recording Form */}
        {showPaymentBox && (
          <form onSubmit={handlePaymentSubmit} style={{ background: "rgba(16, 185, 129, 0.08)", border: "1px solid rgba(16, 185, 129, 0.3)", borderRadius: "var(--radius-md)", padding: "0.75rem", marginBottom: "1rem" }}>
            <div style={{ fontWeight: "700", fontSize: "0.85rem", color: "var(--success)", marginBottom: "0.4rem" }}>
              Record Payment to {purchase.vendorName}
            </div>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <input
                type="number"
                step="any"
                min="1"
                max={balance}
                placeholder={`Amount (max ₹${balance})`}
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                className="form-control"
                required
                style={{ flex: 1, minHeight: "36px" }}
              />
              <Button type="submit" variant="primary" size="sm" disabled={recordingPayment}>
                {recordingPayment ? "Saving..." : "Confirm Pay"}
              </Button>
            </div>
          </form>
        )}

        {/* Action Buttons — same row as invoices */}
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          {!isPaid && (
            <>
              <Button type="button" variant="secondary" style={{ flex: 1, minHeight: "40px" }} onClick={() => setShowPaymentBox(!showPaymentBox)}>
                💳 Record Payment
              </Button>
              <Button type="button" variant="secondary" style={{ flex: 1, minHeight: "40px" }} onClick={() => setShowVendorTallyModal(true)}>
                💸 Settle Bills
              </Button>
            </>
          )}
          {onEditPurchase && (
            <Button type="button" variant="secondary" style={{ flex: 1, minHeight: "40px" }} onClick={() => onEditPurchase(purchase)}>
              ✏️ Edit
            </Button>
          )}
          <Button type="button" variant="primary" style={{ flex: 1, minHeight: "40px" }} onClick={() => setShowPrint(true)}>
            🖨️ Print / PDF
          </Button>
          <Button type="button" variant="danger" style={{ minHeight: "40px" }} onClick={handleDeleteClick} disabled={deleting}>
            {deleting ? "Reversing…" : "🗑️ Delete"}
          </Button>
        </div>
      </div>
    </BottomSheet>

    <PurchasePrintModal isOpen={showPrint} onClose={() => setShowPrint(false)} purchase={purchase} />

    <BillHistorySheet
      isOpen={showHistory}
      onClose={() => setShowHistory(false)}
      bill={purchase}
      billNo={purchase.vendorBillNo || ""}
      loadRevisions={getPurchaseRevisions}
      renderOld={(old, close) => <PurchasePrintModal isOpen onClose={close} purchase={old} watermark="OLD VERSION" />}
    />

    {/* Vendor Multi-Bill Payment Modal */}
    <VendorPaymentModal
      isOpen={showVendorTallyModal}
      onClose={() => setShowVendorTallyModal(false)}
      preselectedVendor={purchase.vendorId ? { id: purchase.vendorId, name: purchase.vendorName } : null}
    />

    {/* Payment Receipt Modal */}
    {paymentCorrections.host}

    <PaymentReceiptModal
      isOpen={!!selectedReceiptPayment}
      onClose={() => setSelectedReceiptPayment(null)}
      payment={selectedReceiptPayment}
    />
  </>
  );
}

export default PurchaseDetailModal;
